import { useEffect, useState } from "react";
import { usePageState } from "@/contexts/NavigationContext";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  ArrowsClockwiseIcon, EyeIcon, FileTextIcon, PlusIcon, TagIcon, TrashIcon, XIcon, EnvelopeIcon,
} from "@phosphor-icons/react";
import { EmailHistory, SendDocEmailModal } from "@/components/DocEmail";
import { useAuth } from "@/contexts/AuthContext";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

export const APROBACION = {
  borrador: ["Borrador", "bg-white/5 text-text-secondary border-border"],
  pendiente: ["Pendiente de aprobación", "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30"],
  devuelta: ["Devuelta para corrección", "bg-orange-500/10 text-orange-400 border-orange-500/30"],
  aprobada: ["Aprobada", "bg-agro-green/10 text-agro-green border-agro-green/30"],
  rechazada: ["Rechazada", "bg-red-500/10 text-red-400 border-red-500/30"],
};

export default function Proformas() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = usePageState("filter", { estado: "" });
  const [converting, setConverting] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [emailing, setEmailing] = useState(null);
  const [revisando, setRevisando] = useState(null);
  const { user } = useAuth();
  const eff = user?.effective || {};
  const esRtc = Boolean(eff.alcance_regional);
  const revisa = !esRtc && (eff.is_admin || eff.revisiones?.proformas);
  const propia = (p) => p.created_by === user?.email;
  const facturable = (p) => !p.estado_aprobacion || p.estado_aprobacion === "aprobada";

  const enviar = async (p) => {
    try { await api.post(`/sales/proformas/${p.id}/enviar`); toast.success(`Proforma ${p.numero} enviada para aprobación`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.estado) params.estado = filter.estado;
      const { data } = await api.get("/sales/proformas", { params });
      setItems(filter.aprobacion ? data.filter((p) => p.estado_aprobacion === filter.aprobacion) : data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const onConvert = async (p) => {
    if (!window.confirm(`Convertir proforma ${p.numero} en factura formal con CAI?\nSe descontará el inventario y se generará el asiento contable.`)) return;
    setConverting(p.id);
    try {
      const { data } = await api.post(`/invoices/${p.id}/convert-to-invoice`);
      toast.success(`Factura ${data.numero} generada desde proforma`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setConverting(null); }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`¿Eliminar la proforma ${p.numero}?\nEsta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/invoices/${p.id}`);
      toast.success(`Proforma ${p.numero} eliminada`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const view = async (p) => {
    const { data } = await api.get(`/invoices/${p.id}`);
    setViewing(data);
  };

  const totalEmitidas = items.filter((p) => p.estado === "emitida").reduce((s, p) => s + (p.total || 0), 0);
  const countConvertidas = items.filter((p) => p.estado === "convertida").length;

  return (
    <Layout
      title="Proformas"
      subtitle={`${items.length} documentos · Convierte a factura formal con un clic`}
      actions={
        <button
          onClick={() => navigate("/ventas/nueva")}
          data-testid="new-proforma-btn"
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 transition-all active:scale-95"
        >
          <PlusIcon size={16} weight="bold" /> Nueva proforma
        </button>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card label="Proformas" value={items.length} />
        <Card label="Total emitidas vigentes" value={fmtL(totalEmitidas)} accent="green" />
        <Card label="Convertidas a factura" value={countConvertidas} accent="yellow" />
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60 flex gap-3 flex-wrap">
          <select
            data-testid="filter-estado"
            value={filter.estado}
            onChange={(e) => setFilter({ ...filter, estado: e.target.value })}
            className="bg-app border border-border rounded-md px-3 py-2 text-sm"
          >
            <option value="">Todos los estados</option>
            <option value="emitida">Emitidas</option>
            <option value="convertida">Convertidas</option>
            <option value="anulada">Anuladas</option>
          </select>
          <select data-testid="filter-aprobacion" value={filter.aprobacion || ""}
            onChange={(e) => setFilter({ ...filter, aprobacion: e.target.value })}
            className="bg-app border border-border rounded-md px-3 py-2 text-sm">
            <option value="">Toda aprobación</option>
            {Object.entries(APROBACION).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">Número</th>
                <th className="p-3">Cliente</th>
                <th className="p-3">Fecha</th>
                <th className="p-3">Cód. promo</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3">Estado</th>
                <th className="p-3 pr-5 text-right">Acc.</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="7" className="p-12 text-center text-text-muted">Sin proformas</td></tr>
              ) : items.map((p) => (
                <tr key={p.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`proforma-row-${p.numero}`}>
                  <td className="p-3 pl-5 font-mono text-xs text-agro-green">{p.numero}</td>
                  <td className="p-3 text-sm">
                    {p.cliente_nombre}
                    <div className="text-[10px] text-text-muted">{p.cliente_rtn || "—"}</div>
                  </td>
                  <td className="p-3 text-xs text-text-secondary">{fmtDate(p.fecha)}</td>
                  <td className="p-3 text-xs">
                    {p.codigo_promocional ? (
                      <span className="inline-flex items-center gap-1 bg-agro-yellow/10 text-agro-yellow border border-agro-yellow/30 rounded px-2 py-0.5">
                        <TagIcon size={10} weight="bold" /> {p.codigo_promocional}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="p-3 text-right text-sm font-medium">{fmtL(p.total)}</td>
                  <td className="p-3">
                    <EstadoBadge estado={p.estado} converted={p.convertida_factura_numero} />
                    {p.estado_aprobacion && (
                      <span data-testid={`aprobacion-${p.numero}`}
                        className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded border ${APROBACION[p.estado_aprobacion]?.[1] || "border-border"}`}>
                        {APROBACION[p.estado_aprobacion]?.[0] || p.estado_aprobacion}
                      </span>
                    )}
                    {p.observaciones_revision && ["devuelta", "rechazada"].includes(p.estado_aprobacion) && (
                      <div className="text-[10px] text-text-muted mt-0.5 max-w-[220px]">“{p.observaciones_revision}”</div>
                    )}
                  </td>
                  <td className="p-3 pr-5 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => view(p)} className="p-1.5 text-text-muted hover:text-agro-green" title="Ver" aria-label="Ver">
                        <EyeIcon size={16} />
                      </button>
                      <button
                        onClick={() => navigate(`/ventas/${p.id}/imprimir`)}
                        data-testid={`print-btn-${p.numero}`}
                        className="p-1.5 text-text-muted hover:text-agro-green"
                        title="Imprimir"
                      >
                        <FileTextIcon size={16} />
                      </button>
                      <button
                        onClick={() => setEmailing(p)}
                        data-testid={`email-btn-${p.numero}`}
                        className="p-1.5 text-text-muted hover:text-agro-green"
                        title="Enviar proforma por correo"
                      >
                        <EnvelopeIcon size={16} />
                      </button>
                      {propia(p) && ["borrador", "devuelta"].includes(p.estado_aprobacion) && (
                        <>
                          <button onClick={() => navigate(`/ventas/proforma/${p.id}/editar`)} data-testid={`edit-btn-${p.numero}`}
                            className="px-2.5 py-1 text-[10px] uppercase tracking-wider border border-border rounded hover:border-agro-green/40">Editar</button>
                          <button onClick={() => enviar(p)} data-testid={`send-btn-${p.numero}`}
                            className="px-2.5 py-1 text-[10px] uppercase tracking-wider bg-agro-yellow/10 text-agro-yellow border border-agro-yellow/30 rounded">Enviar a aprobación</button>
                        </>
                      )}
                      {revisa && p.estado_aprobacion === "pendiente" && (
                        <button onClick={() => setRevisando(p)} data-testid={`review-btn-${p.numero}`}
                          className="px-2.5 py-1 text-[10px] uppercase tracking-wider bg-agro-yellow/10 text-agro-yellow border border-agro-yellow/30 rounded">Revisar</button>
                      )}
                      {p.estado === "emitida" && !esRtc && facturable(p) && (
                        <button
                          onClick={() => onConvert(p)}
                          disabled={converting === p.id}
                          data-testid={`convert-btn-${p.numero}`}
                          className="px-2.5 py-1 text-[10px] uppercase tracking-wider bg-agro-green/10 text-agro-green border border-agro-green/30 rounded hover:bg-agro-green/20 flex items-center gap-1 disabled:opacity-50"
                          title="Convertir a factura formal"
                        >
                          <ArrowsClockwiseIcon size={12} weight="bold" />
                          {converting === p.id ? "..." : "A factura"}
                        </button>
                      )}
                      {p.estado !== "convertida" && (!esRtc || (propia(p) && ["borrador", "devuelta"].includes(p.estado_aprobacion))) && (
                        <button
                          onClick={() => onDelete(p)}
                          data-testid={`delete-btn-${p.numero}`}
                          className="p-1.5 text-text-muted hover:text-red-400 hover:bg-red-500/10 rounded"
                          title="Eliminar proforma"
                        >
                          <TrashIcon size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {viewing && <ProformaViewer p={viewing} onClose={() => setViewing(null)} onEmail={() => setEmailing(viewing)} />}
      {revisando && <RevisionModal p={revisando} onClose={() => setRevisando(null)} onDone={() => { setRevisando(null); load(); }} />}
      {emailing && (
        <SendDocEmailModal title="Enviar proforma por email" docLabel="Proforma"
          endpoint={`/invoices/${emailing.id}/send-email`} clienteId={emailing.cliente_id}
          defaultSubject={`Proforma ${emailing.numero} · Agroport Honduras`}
          onClose={() => setEmailing(null)}
          onSent={() => { if (viewing && viewing.id === emailing.id) view(viewing); load(); }} />
      )}
    </Layout>
  );
}

function Card({ label, value, accent }) {
  const color = accent === "green" ? "text-agro-green" : accent === "yellow" ? "text-agro-yellow" : "text-text-primary";
  return (
    <div className="bg-card border border-border/60 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`font-heading text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function EstadoBadge({ estado, converted }) {
  const map = {
    emitida: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    convertida: "bg-agro-green/10 text-agro-green border-agro-green/30",
    anulada: "bg-red-500/10 text-red-400 border-red-500/30",
  };
  return (
    <div>
      <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${map[estado] || "border-border"}`}>
        {estado}
      </span>
      {converted && <div className="text-[10px] text-text-muted mt-0.5">→ {converted}</div>}
    </div>
  );
}

function ProformaViewer({ p, onClose, onEmail }) {
  const m = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="proforma-viewer">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Proforma</div>
            <h3 className="font-heading text-xl font-semibold">{p.numero}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onEmail} data-testid="proforma-viewer-email"
              className="px-3 py-2 bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md text-xs flex items-center gap-1.5">
              <EnvelopeIcon size={14} /> Enviar por email
            </button>
            <button aria-label="Cerrar" onClick={m.cerrar} className="p-2 text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
          </div>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase text-text-muted">Cliente</div>
              <div className="font-medium">{p.cliente_nombre}</div>
              <div className="text-xs text-text-secondary">RTN: {p.cliente_rtn || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-text-muted">Vendedor / Región</div>
              <div className="text-sm">{p.vendedor || "—"} {p.region && `· ${p.region}`}</div>
              <div className="text-xs text-text-secondary">Tipo: {p.tipo_venta}</div>
            </div>
          </div>
          {p.codigo_promocional && (
            <div className="bg-agro-yellow/10 border border-agro-yellow/30 rounded-md p-3 flex items-center gap-2">
              <TagIcon size={14} className="text-agro-yellow" />
              <span className="text-xs">Código promocional: <strong className="text-agro-yellow font-mono">{p.codigo_promocional}</strong></span>
            </div>
          )}
          <table className="w-full">
            <thead>
              <tr className="text-left text-[10px] uppercase text-text-muted border-b border-border">
                <th className="p-2">Producto</th><th className="p-2 text-right">Cant.</th>
                <th className="p-2 text-right">Precio</th><th className="p-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {p.items?.map((it, i) => (
                <tr key={i} className="border-b border-border/30">
                  <td className="p-2 text-xs">{it.codigo} — {it.descripcion}</td>
                  <td className="p-2 text-right text-sm">{it.cantidad}</td>
                  <td className="p-2 text-right text-sm">{fmtL(it.precio_unitario)}</td>
                  <td className="p-2 text-right text-sm font-medium">{fmtL(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-border pt-3 flex justify-between items-baseline">
            <span className="text-xs uppercase tracking-wider text-text-muted">Total</span>
            <span className="font-heading text-2xl text-agro-green">{fmtL(p.total)}</span>
          </div>
          {p.notas && <div className="text-xs text-text-secondary"><strong>Notas:</strong> {p.notas}</div>}
          {p.estado_aprobacion && (
            <div className="border-t border-border pt-3" data-testid="proforma-aprobacion">
              <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">
                Aprobación: <span className="text-text-primary">{APROBACION[p.estado_aprobacion]?.[0] || p.estado_aprobacion}</span>
              </div>
              {p.observaciones_revision && <div className="text-xs mb-1">Observaciones: {p.observaciones_revision}</div>}
              <ul className="text-[11px] text-text-muted space-y-0.5">
                {(p.aprobacion_historial || []).map((h, i) => (
                  <li key={i}>{fmtDate(h.fecha)} · {h.accion} · {h.por}{h.observaciones ? ` — ${h.observaciones}` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          <EmailHistory items={p.email_history} />
        </div>
      </div>
    </div>
  );
}

export function RevisionModal({ p, onClose, onDone, sinAprobar = false }) {
  const [obs, setObs] = useState("");
  const [saving, setSaving] = useState(false);
  const decidir = async (accion) => {
    if (accion !== "aprobar" && !obs.trim()) return toast.error(accion === "rechazar" ? "Indica el motivo del rechazo" : "Indica qué se debe corregir");
    setSaving(true);
    try {
      await api.post(`/sales/proformas/${p.id}/revisar`, { accion, observaciones: obs });
      toast.success({ aprobar: "Proforma aprobada", rechazar: "Proforma rechazada", devolver: "Proforma devuelta para corrección" }[accion]);
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  const m = useModalForm({ value: obs, onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="proforma-review-modal">
        <h3 className="font-heading text-lg">{sinAprobar ? `DEVOLVER O RECHAZAR PROFORMA ${p.numero}` : `Revisar proforma ${p.numero}`}</h3>
        <div className="text-xs text-text-secondary">{p.cliente_nombre} · {fmtL(p.total)} · enviada por {p.created_by}</div>
        <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={3} data-testid="review-obs"
          placeholder="Observaciones (obligatorias para devolver o rechazar)"
          className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" />
        <div className="flex justify-end gap-2 flex-wrap">
          <button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded text-text-secondary">Cancelar</button>
          <button disabled={saving} onClick={() => decidir("rechazar")} data-testid="review-reject" className="px-3 py-1.5 text-sm border border-red-500/40 text-red-400 rounded">Rechazar</button>
          <button disabled={saving} onClick={() => decidir("devolver")} data-testid="review-return" className="px-3 py-1.5 text-sm border border-orange-500/40 text-orange-400 rounded">Devolver</button>
          {!sinAprobar && <button disabled={saving} onClick={() => decidir("aprobar")} data-testid="review-approve" className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded">Aprobar</button>}
        </div>
      </div>
    </div>
  );
}

export function AprobacionBadge({ p }) {
  if (!p.estado_aprobacion) return null;
  return (
    <div>
      <span data-testid={`aprobacion-${p.numero}`}
        className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded border ${APROBACION[p.estado_aprobacion]?.[1] || "border-border"}`}>
        {APROBACION[p.estado_aprobacion]?.[0] || p.estado_aprobacion}
      </span>
      {p.observaciones_revision && ["devuelta", "rechazada"].includes(p.estado_aprobacion) && (
        <div className="text-[10px] text-text-muted mt-0.5 max-w-[220px]">“{p.observaciones_revision}”</div>
      )}
    </div>
  );
}

export function AprobacionInfo({ p }) {
  if (!p.estado_aprobacion) return null;
  return (
    <div className="border-t border-border pt-3" data-testid="proforma-aprobacion">
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">
        Aprobación: <span className="text-text-primary">{APROBACION[p.estado_aprobacion]?.[0] || p.estado_aprobacion}</span>
      </div>
      {p.observaciones_revision && <div className="text-xs mb-1">Observaciones: {p.observaciones_revision}</div>}
      <ul className="text-[11px] text-text-muted space-y-0.5">
        {(p.aprobacion_historial || []).map((h, i) => (
          <li key={i}>{fmtDate(h.fecha)} · {h.accion} · {h.por}{h.observaciones ? ` — ${h.observaciones}` : ""}</li>
        ))}
      </ul>
    </div>
  );
}
